<script lang="ts">
import type { AccordionEmits, AccordionItem, AccordionProps, AccordionSlots } from '#ui/types';
import { tv } from 'tailwind-variants';
import theme from '#build/ui/accordion';
</script>

<script setup lang="ts" generic="T extends AccordionItem">
import { useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useAppConfig } from '#app/config';
import { omit } from '#layers/base/public';
import Accordion from '#ui/components/Accordion.vue';

const props = defineProps<AccordionProps<T>>();
const emits = defineEmits<AccordionEmits>();
const slots = defineSlots<AccordionSlots<T>>();

const forwarded = useForwardPropsEmits(props, emits);
const proxySlots = omit(slots, ['trailing', 'leading']);
const appConfig = useAppConfig();
const ui = computed(() => tv({ extend: tv(theme), ...appConfig.ui?.accordion })({
	disabled: props.disabled,
}));
</script>

<template>
	<Accordion v-bind="forwarded">
		<template #leading="{ item, index, open }">
			<!-- @vue-ignore TODO figure out why this is needed -->
			<slot name="leading" v-bind="{ item, index, open, ui }">
				<DIcon
					:name="trailingIcon ?? 'lucide:chevron-right'"
					:class="ui.leadingIcon({
						class: [
							'size-5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90',
							props.ui?.leadingIcon,
							item?.ui?.leadingIcon,
						],
					})"
				/>
			</slot>
		</template>
		<template v-for="(_, name) in proxySlots" #[name]="slotData">
			<!-- TODO this might get better with a new Nuxt UI release -->
			<!-- @vue-expect-error -->
			<slot :name v-bind="slotData" />
		</template>
		<template #trailing="{ item, index, open }">
			<span class="ms-auto">
				<!-- @vue-ignore TODO figure out why this is needed -->
				<slot name="trailing" v-bind="{ item, index, open, ui }">
					<DIcon v-if="item.icon" :name="item.icon" class="size-5 shrink-0" />
				</slot>
			</span>
		</template>
	</Accordion>
</template>
